import { motion } from "framer-motion";
import { AlertTriangle, Clock, TrendingDown, DollarSign } from "lucide-react";
import { useCountUp } from "@/hooks/useCountUp";

const problems = [
  {
    icon: AlertTriangle,
    title: "High-Stakes, Little Practice",
    description: "Employees face critical conversations unprepared. One bad negotiation or feedback session can cost relationships, deals, and talent.",
    stat: 68,
    statSuffix: "%",
    statLabel: "of managers avoid difficult conversations"
  },
  {
    icon: Clock,
    title: "Training Doesn't Scale",
    description: "Role-play workshops require scheduling, facilitators, and coordination. They're expensive, inconsistent, and impossible to repeat on demand.",
    stat: 1200,
    statPrefix: "$",
    statLabel: "average cost per person per workshop"
  },
  {
    icon: TrendingDown,
    title: "No Measurable Outcomes",
    description: "Traditional training lacks data. There's no way to track improvement, compare performance, or prove ROI to leadership.",
    stat: 12,
    statSuffix: "%",
    statLabel: "of training transfers to on-the-job behavior"
  },
  {
    icon: DollarSign,
    title: "Lost Revenue from Weak Skills",
    description: "Poor negotiation and sales conversations directly impact the bottom line. Teams leave money on the table daily.",
    stat: 2.5,
    statPrefix: "$",
    statSuffix: "M",
    statLabel: "annual revenue lost per 100 sales reps"
  }
];

function StatCard({ problem, index }) {
  const targetVal = problem.stat === 2.5 ? 250 : problem.stat;
  const { count, ref } = useCountUp(targetVal, 1500);

  const formatStat = () => {
    if (problem.stat === 2.5) {
      return `${problem.statPrefix || ""}${(count / 100).toFixed(1)}${problem.statSuffix || ""}`;
    }
    return `${problem.statPrefix || ""}${count.toLocaleString()}${problem.statSuffix || ""}`;
  };

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.1, ease: [0.22, 1, 0.36, 1] }}
      className="group stripe-card rounded-2xl p-8 hover:border-stripe-primary/20"
    >
      <div className="flex items-start justify-between mb-6">
        <div className="w-12 h-12 rounded-xl bg-red-50 flex items-center justify-center group-hover:bg-red-100 group-hover:scale-105 transition-all duration-300">
          <problem.icon className="w-6 h-6 text-red-500" />
        </div>
        <div className="text-right">
          <div className="font-heading text-2xl font-bold text-navy">
            {formatStat()}
          </div>
          <div className="text-xs text-stripe-text-muted max-w-[140px]">{problem.statLabel}</div>
        </div>
      </div>
      <h3 className="font-heading text-lg font-semibold text-navy mb-2">{problem.title}</h3>
      <p className="text-sm text-stripe-text-secondary leading-relaxed max-w-[30rem]">{problem.description}</p>
    </motion.div>
  );
}

export default function ProblemSection() {
  return (
    <section id="problem" data-testid="problem-section" className="py-[45px] md:py-[90px] stripe-bg relative">
      <div className="absolute inset-0 section-glow-left" />
      <div className="max-w-7xl mx-auto px-6 md:px-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-2xl mb-16"
        >
          <div className="section-overline mb-4">The Problem</div>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-navy mb-6">
            Soft skills are critical.
            <br />
            <span className="text-stripe-text-muted">Training them is broken.</span>
          </h2>
          <p className="text-base md:text-lg text-stripe-text-secondary leading-relaxed max-w-[39rem]">
            The skills that matter most in business — negotiation, persuasion, empathy — are the hardest to practice and almost impossible to measure.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {problems.map((p, i) => (
            <StatCard key={i} problem={p} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
